import { BaseRepository } from '@/base/base-repository';
import { APP_CFG } from '@/configs';
import { CoinFlipEvent, CoinflipLegacyEvent, LimboEvent, LootboxEvent, PlinkoEvent, RangeEvent, SuiroyaleEvent } from '@/dtos';
import { toRoundFixed, UQ32ToFloat } from '@/utils';
import { EventId, SuiClient } from '@mysten/sui/client';

const DEFAULT_COIN_TYPE = '0x2::sui::SUI';

export interface Activity {
	txId: string;
	betId: string;
	gambler: string;
	win: boolean;
	reward: number;
	stake: number;
	from: string;
	at: number;
	payout_multiplier: number;
	coin_type?: string;
	gameDetails?: Record<string, any>;
	coinflipOutcome?: string;

	random_number?: number;
	roll_under?: boolean;

	multiplier?: { pos0: string };
	target_multiplier?: { pos0: string };
}

const toActivityTimestamp = (timestamp: string | number | null | undefined): number => {
	if (typeof timestamp === 'number') {
		return timestamp;
	}
	if (typeof timestamp === 'string') {
		return Number(timestamp);
	}
	return Date.now();
};

const mapCoinflipLegacyEvent = (event: CoinflipLegacyEvent, from: string): Activity => {
	const reward = Number(event.parsedJson.reward);
	const stake = Number(event.parsedJson.stake);
	const payoutMultiplier = toRoundFixed(UQ32ToFloat(event.parsedJson.payout_multiplier));
	const winField = event.parsedJson.win;
	const win = typeof winField === 'boolean' ? winField : typeof winField === 'string' ? winField.toLowerCase() === 'true' : reward > 0;

	return {
		txId: event.id.txDigest,
		betId: event.parsedJson.bet_id,
		gambler: event.parsedJson.gambler,
		win,
		reward,
		stake,
		payout_multiplier: payoutMultiplier,
		from,
		at: toActivityTimestamp(event.timestampMs),
		coin_type: DEFAULT_COIN_TYPE,
	};
};

const mapLootboxEvent = (event: LootboxEvent, from: string): Activity => ({
	txId: event.id.txDigest,
	betId: event.parsedJson.bet_id,
	gambler: event.parsedJson.gambler,
	win: event.parsedJson.reward > 0,
	reward: Number(event.parsedJson.reward),
	stake: Number((event.parsedJson as any).stake),
	payout_multiplier: toRoundFixed(UQ32ToFloat((event.parsedJson as any).payout_multiplier)),
	from,
	at: toActivityTimestamp(event.timestampMs),
	coin_type: DEFAULT_COIN_TYPE,
});

const mapLimboEvent = (event: LimboEvent, from: string): Activity => ({
	txId: event.id.txDigest,
	betId: event.parsedJson.bet_id,
	gambler: event.parsedJson.gambler,
	win: event.parsedJson.win,
	reward: Number(event.parsedJson.reward),
	stake: Number(event.parsedJson.stake),
	multiplier: event.parsedJson.multiplier,
	target_multiplier: event.parsedJson.target_multiplier,
	payout_multiplier: toRoundFixed(UQ32ToFloat(event.parsedJson.payout_multiplier)),
	from,
	at: toActivityTimestamp(event.timestampMs),
	coin_type: DEFAULT_COIN_TYPE,
});

const mapDiceEvent = (event: RangeEvent, from: string): Activity => ({
	txId: event.id.txDigest,
	betId: (event.parsedJson as any).bet_id,
	gambler: event.parsedJson.gambler,
	win: event.parsedJson.win,
	reward: Number(event.parsedJson.reward),
	stake: Number(event.parsedJson.stake),
	random_number: event.parsedJson.random_number,
	roll_under: event.parsedJson.roll_under,
	payout_multiplier: toRoundFixed(UQ32ToFloat(event.parsedJson.payout_multiplier)),
	from,
	at: toActivityTimestamp(event.timestampMs),
	coin_type: DEFAULT_COIN_TYPE,
});

const mapPlinkoEvent = (event: PlinkoEvent, from: string): Activity => ({
	txId: event.id.txDigest,
	betId: (event.parsedJson as any).bet_id,
	gambler: (event.parsedJson as any).gambler,
	win: Number((event.parsedJson as any).reward) > 0,
	reward: Number((event.parsedJson as any).reward),
	stake: Number((event.parsedJson as any).stake),
	multiplier: (event.parsedJson as any).multiplier,
	payout_multiplier: toRoundFixed(UQ32ToFloat((event.parsedJson as any).payout_multiplier)),
	from,
	at: toActivityTimestamp(event.timestampMs),
	coin_type: DEFAULT_COIN_TYPE,
});

const mapSuiroyaleEvent = (event: SuiroyaleEvent, from: string): Activity => ({
	txId: event.id.txDigest,
	betId: event.parsedJson.bet_id,
	gambler: event.parsedJson.gambler,
	win: event.parsedJson.win,
	reward: Number(event.parsedJson.reward),
	stake: Number(event.parsedJson.stake),
	multiplier: event.parsedJson.multiplier,
	payout_multiplier: toRoundFixed(UQ32ToFloat(event.parsedJson.payout_multiplier)),
	from,
	at: toActivityTimestamp(event.timestampMs),
	coin_type: DEFAULT_COIN_TYPE,
});

class ActivityRepository extends BaseRepository {
	async getCoinflipActivities(client: SuiClient): Promise<Activity[]> {
		const response = await client.queryEvents({
			cursor: undefined,
			limit: 50,
			order: 'descending',
			query: {
				MoveEventType: `${APP_CFG.PACKAGE_2}::events::CoinFlipRevealedEvent`,
			},
		});

		return (response.data as CoinflipLegacyEvent[]).map((event) => mapCoinflipLegacyEvent(event, 'CoinFlip'));
	}

	async getLootboxActivities(client: SuiClient): Promise<Activity[]> {
		const response = await client.queryEvents({
			cursor: undefined,
			limit: 100,
			order: 'descending',
			query: {
				MoveEventType: `${APP_CFG.PACKAGE_2}::events::LootboxRevealedEvent`,
			},
		});

		return (response.data as LootboxEvent[]).map((event) => mapLootboxEvent(event, 'LootBox'));
	}

	async getLimboActivities(client: SuiClient): Promise<Activity[]> {
		const response = await client.queryEvents({
			cursor: undefined,
			limit: 100,
			order: 'descending',
			query: {
				MoveEventType: `${APP_CFG.PACKAGE_2}::events::LimboRevealedEvent`,
			},
		});

		return (response.data as LimboEvent[]).map((event) => mapLimboEvent(event, 'Limbo'));
	}

	async getDiceActivities(client: SuiClient): Promise<Activity[]> {
		const response = await client.queryEvents({
			cursor: undefined,
			limit: 100,
			order: 'descending',
			query: {
				MoveEventType: `${APP_CFG.PACKAGE_2}::events::RevealedDiceBetEvent`,
			},
		});

		return (response.data as RangeEvent[]).map((event) => mapDiceEvent(event, 'Dice'));
	}

	async getPlinkoActivities(client: SuiClient): Promise<Activity[]> {
		const response = await client.queryEvents({
			cursor: undefined,
			limit: 100,
			order: 'descending',
			query: {
				MoveEventType: `${APP_CFG.PACKAGE_2}::events::PlinkoRevealedEvent`,
			},
		});

		return (response.data as PlinkoEvent[]).map((event) => mapPlinkoEvent(event, 'Plinko'));
	}

	async getSuiroyaleActivities(client: SuiClient): Promise<Activity[]> {
		const response = await client.queryEvents({
			cursor: undefined,
			limit: 100,
			order: 'descending',
			query: {
				MoveEventType: `${APP_CFG.PACKAGE_2}::events::SuiroyaleRevealedEvent`,
			},
		});

		return (response.data as SuiroyaleEvent[]).map((event) => mapSuiroyaleEvent(event, 'Suiroyale'));
	}

	async getTopActivities(client: SuiClient, gameType: string): Promise<Activity[]> {
		const queryMap: Record<string, string> = {
			coinflip: `${APP_CFG.PACKAGE_2}::events::CoinflipRevealedEvent`,
			lootbox: `${APP_CFG.PACKAGE_2}::events::LootboxRevealedEvent`,
			limbo: `${APP_CFG.PACKAGE_2}::events::LimboRevealedEvent`,
			dice: `${APP_CFG.PACKAGE_2}::events::RevealedDiceBetEvent`,
			plinko: `${APP_CFG.PACKAGE_2}::events::PlinkoRevealedEvent`,
			suiroyale: `${APP_CFG.PACKAGE_2}::events::SuiroyaleRevealedEvent`,
		};

		const query = queryMap[gameType];
		if (!query) {
			throw new Error(`Invalid game type: ${gameType}`);
		}

		const activities: Activity[] = [];
		let hasNextPage = true;
		let nextCursor: EventId | null | undefined = undefined;

		while (hasNextPage && activities.length < 400) {
			const {
				data,
				hasNextPage: pageHasNext,
				nextCursor: pageNextCursor,
			} = await client.queryEvents({
				cursor: nextCursor,
				limit: 50,
				order: 'descending',
				query: { MoveEventType: query },
			});

			const mappedActivities = data.map((event: any) => {
				if (gameType === 'coinflip') {
					return mapCoinflipLegacyEvent(event as CoinflipLegacyEvent, gameType);
				}

				switch (gameType) {
					case 'lootbox':
						return mapLootboxEvent(event as LootboxEvent, gameType);
					case 'limbo':
						return mapLimboEvent(event as LimboEvent, gameType);
					case 'dice':
						return mapDiceEvent(event as RangeEvent, gameType);
					case 'plinko':
						return mapPlinkoEvent(event as PlinkoEvent, gameType);
					case 'suiroyale':
						return mapSuiroyaleEvent(event as SuiroyaleEvent, gameType);
					default:
						throw new Error(`Unsupported game type: ${gameType}`);
				}
			});

			activities.push(...mappedActivities);
			hasNextPage = pageHasNext;
			nextCursor = pageNextCursor;

			await new Promise((resolve) => setTimeout(resolve, 250));
		}

		return activities
			.filter((activity) => activity.win && activity.reward - activity.stake >= 10 && activity.payout_multiplier >= 2)
			.sort((a, b) => b.payout_multiplier - a.payout_multiplier)
			.slice(0, 2);
	}

	async getRecentWinsByAddress(client: SuiClient, address: string, limit = 20): Promise<Activity[]> {
		if (!address) {
			return [];
		}

		const collected: Activity[] = [];
		let cursor: EventId | null | undefined = undefined;
		let hasNextPage = true;

		const resolveActivity = (event: any): Activity | null => {
			const typeText = typeof event?.type === 'string' ? event.type : '';
			const normalizedType = typeText.toLowerCase();

			try {
				if (normalizedType.includes('coinflip')) {
					return mapCoinflipLegacyEvent(event as CoinFlipEvent, 'CoinFlip');
				}
				if (normalizedType.includes('lootbox')) {
					return mapLootboxEvent(event as LootboxEvent, 'LootBox');
				}
				if (normalizedType.includes('limbo')) {
					return mapLimboEvent(event as LimboEvent, 'Limbo');
				}
				if (normalizedType.includes('plinko')) {
					return mapPlinkoEvent(event as PlinkoEvent, 'Plinko');
				}
				if (normalizedType.includes('suiroyale')) {
					return mapSuiroyaleEvent(event as SuiroyaleEvent, 'Suiroyale');
				}
				if (normalizedType.includes('dice')) {
					return mapDiceEvent(event as RangeEvent, 'Dice');
				}
			} catch {
				// Ignore mapping errors
			}

			return null;
		};

		while (hasNextPage && collected.length < limit) {
			const {
				data,
				hasNextPage: pageHasNext,
				nextCursor,
			} = await client.queryEvents({
				cursor,
				limit: 50,
				order: 'descending',
				query: { Sender: address },
			});

			for (const rawEvent of data) {
				const activity = resolveActivity(rawEvent);
				if (!activity) {
					continue;
				}

				const net = activity.reward - activity.stake;
				if (net > 0) {
					collected.push(activity);
					if (collected.length >= limit) {
						break;
					}
				}
			}

			if (collected.length >= limit) {
				break;
			}

			cursor = nextCursor;
			hasNextPage = pageHasNext && nextCursor != null;

			if (hasNextPage) {
				await new Promise((resolve) => setTimeout(resolve, 150));
			}
		}

		return collected.slice(0, limit);
	}
}

export const activityRepository = new ActivityRepository();
